import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import api from '../../services/api';
import { toast } from 'react-toastify';

const EditUnitModal = ({ show, handleClose, handleSave, unit }) => {
  // State for form fields
  const [projectId, setProjectId] = useState('');
  const [type, setType] = useState('');
  const [price, setPrice] = useState('');
  const [area, setArea] = useState('');
  const [location, setLocation] = useState('');
  const [picture, setPicture] = useState(null);
  const [numberOfBedrooms, setNumberOfBedrooms] = useState('');
  const [numberOfBathrooms, setNumberOfBathrooms] = useState('');
  const [finishing, setFinishing] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  // Update form fields when unit changes (for editing)
  useEffect(() => {
    if (unit) {
      setProjectId(unit?.project_id || '');
      setType(unit?.type || '');
      setPrice(unit?.price || '');
      setArea(unit?.area || '');
      setLocation(unit?.location || '');
      setNumberOfBedrooms(unit?.numberOfBedrooms || '');
      setNumberOfBathrooms(unit?.numberOfBathrooms || '');
      setFinishing(unit?.finishing || '');
      setImagePreviewUrl(unit?.picture || '');
    }
  }, [unit]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    const maxSize = 2 * 1024 * 1024; // 2MB limit

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, PNG, and PDF files are allowed.");
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      toast.error(`File size is too large (${fileSizeMB}MB). Please compress the image or choose a smaller file (max 2MB).`, { autoClose: 5000 });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1]; // Extract Base64 data
      const mimeType = file.type;
      const formattedBase64 = `data:${mimeType};base64,${base64String}`;

      setPicture(formattedBase64);

      // Only show preview for images, not PDFs
      if (mimeType.startsWith("image/")) {
        setImagePreviewUrl(formattedBase64);
      } else {
        setImagePreviewUrl(null);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleSaveClick = async () => {
    if (!unit) return;

    const updatedFields = {};
    if (projectId !== (unit.project_id || '')) updatedFields.project_id = projectId;
    if (type !== (unit.type || '')) updatedFields.type = type;
    if (price !== (unit.price || '')) updatedFields.price = price;
    if (area !== (unit.area || '')) updatedFields.area = area;
    if (location !== (unit.location || '')) updatedFields.location = location;
    if (numberOfBedrooms !== (unit.numberOfBedrooms || '')) updatedFields.numberOfBedrooms = numberOfBedrooms;
    if (numberOfBathrooms !== (unit.numberOfBathrooms || '')) updatedFields.numberOfBathrooms = numberOfBathrooms;
    if (finishing !== (unit.finishing || '')) updatedFields.finishing = finishing;

    // Attach properly formatted Base64 image if uploaded
    if (picture) {
      updatedFields.picture = picture;
    }
    setLoading(true);
    try {
      await api.put(`/units/${unit.id}`, updatedFields);
      handleSave(updatedFields);
      handleClose();
      toast.success("Unit updated successfully! Wait for approval.");
    } catch (error) {
      console.error("Error updating unit:", error);
      if (error.response) {
        if (error.response.status === 413) {
          // Handle payload too large error
          toast.error("The image you've uploaded is too big. Please compress the image or choose a smaller file and try again.", { autoClose: 6000 });
        } else if (error.response.data?.message) {
          // Check if the error message contains size-related keywords
          const message = error.response.data.message.toLowerCase();
          if (message.includes('too large') || message.includes('size') || message.includes('payload')) {
            toast.error("Image is too large! Please compress your image or reduce its size and try again.", { autoClose: 6000 });
          } else {
            toast.error(`Error: ${error.response.data.message}`);
          }
        } else {
          toast.error(`An error occurred (Status: ${error.response.status}).`);
        }
      } else if (error.request) {
        toast.error("Network Error: Could not connect to the server.");
      } else {
        toast.error("An unexpected error occurred while updating the unit.");
      }
    }
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Unit</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3" controlId="type">
            <Form.Label>Type</Form.Label>
            <Form.Select value={type} onChange={(e) => setType(e.target.value)} required>
              <option value="">Select Unit Type</option>
              <option value="duplex">Duplex</option>
              <option value="apartment">Apartment</option>
              <option value="villa">Villa</option>
              <option value="studio">Studio</option>
              <option value="townhouse">Townhouse</option>
              <option value="twinhouse">Twinhouse</option>
              <option value="standalone villa">Standalone Villa</option>
              <option value="chalet">Chalet</option>
              <option value="cabin">Cabin</option>
              <option value="office">Office</option>
              <option value="clinic">Clinic</option>
              <option value="retail">Retail</option>
              <option value="serviced studio">Serviced Studio</option>
              <option value="serviced apartment">Serviced Apartment</option>
              <option value="branded villa">Branded Villa</option>
              <option value="branded apartment">Branded Apartment</option>
              <option value="beach house">Beach House</option>
              <option value="iVilla">iVilla</option>

            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="price">
            <Form.Label>Price</Form.Label>
            <Form.Control type="number" placeholder="Enter price" value={price} onChange={(e) => setPrice(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="area">
            <Form.Label>Area</Form.Label>
            <Form.Control type="text" placeholder="Enter area (e.g., 120 sqm)" value={area} onChange={(e) => setArea(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="location">
            <Form.Label>Location</Form.Label>
            <Form.Control type="text" placeholder="Enter location of unit" value={location} onChange={(e) => setLocation(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="numberOfBedrooms">
            <Form.Label>Number of Bedrooms</Form.Label>
            <Form.Control type="number" placeholder="Enter number of bedrooms" value={numberOfBedrooms} onChange={(e) => setNumberOfBedrooms(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="numberOfBathrooms">
            <Form.Label>Number of Bathrooms</Form.Label>
            <Form.Control type="number" placeholder="Enter number of bathrooms" value={numberOfBathrooms} onChange={(e) => setNumberOfBathrooms(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="finishing">
            <Form.Label>Finishing</Form.Label>
            <Form.Select value={finishing} onChange={(e) => setFinishing(e.target.value)} required>
              <option value="">Select finishing</option>
              <option value="unfinished">Unfinished</option>
              <option value="Semi Finished">Semi Finished</option>
              <option value="Fully Finished">Fully Finished</option>
              <option value="Fully Finished With ACs">Fully Finished With ACs</option>
              <option value="Fully Finished with ACs and Kitchen Cabinets">Fully Finished with ACs and Kitchen Cabinets</option>
              <option value="Fully Furnished">Fully Furnished</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="picture">
            <Form.Label>Unit Picture (max 2mb)</Form.Label>
            <Form.Control type="file" accept="image/*,application/pdf" onChange={handleFileChange} />
            {imagePreviewUrl && <img src={imagePreviewUrl} alt="Preview" style={{ maxWidth: '150px', marginTop: '10px' }} />}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
        <Button variant="primary" onClick={handleSaveClick} disabled={loading}>{loading ? <Spinner animation="border" size="sm" /> : 'Save Changes'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditUnitModal;
